import React, { useContext, useState, useEffect, useCallback } from "react";
import { Container } from "@material-ui/core";
import { useNavigate } from "react-router";
import { GetlifeContext } from "../../../context/GetLifeContext.context";
import { I18nContext } from "../../../context/i18n.context";
import { GetLifeTrackerContext } from "../../../context/GetLifeTracker.context";
import { StatusContext } from "../../../context/StatusContext";
import { FormBuilderProvider } from "../../../context/FormBuilder.context";
import {
  trackQuestionAnswered,
  trackQuestionViewed,
  trackSubmissionError,
} from "../../../helpers/Tracking";
import HttpLeadRepository from "../../../api/request/Lead/Lead.service";
import FormBuilder from "../../../components/Components/FormBuilder/FormBuilder";
import FormButton from "../../../components/Components/FormBuilder/FormButtons";
import { useQuestionTree } from "./hooks";
import { QuestionTreeResponse, Question } from "./types";
import { QuestionsSummary } from "./components";
import "../css/questions.scss";
import { DOMAIN } from "../../../constants/Global";
import {
  QuestionTreeErrorKindT,
  QuestionTreeErrorPanel,
  questionTreeErrorKind,
} from "../QuestionTreeError";

/**
 * Adapta una pregunta del árbol al formato que espera FormBuilder
 */
const adaptQuestionForFormBuilder = (question: Question) => {
  return {
    questionId: question.id,
    name: question.name,
    type: question.type,
    content: question.content,
    subtitle: question.subtitle || "",
    tooltip: question.tooltip || "",
    componentName: question.component_name || "none",
    field: question.field,
    required: question.required,
    answers: question.options?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) || [],
  };
};

const ProductQuestionsV2: React.FC = () => {
  const navigate = useNavigate();

  // Contextos
  const {
    leadId,
    token,
    loading: globalLoading,
    setLoading,
    setProgressBar,
  } = useContext(GetlifeContext);

  const {
    state: { translate },
  } = useContext(I18nContext);

  const { handleTrackerQuestion } = useContext(GetLifeTrackerContext);
  const { getStatus } = useContext(StatusContext);

  // Estado local
  const [treeData, setTreeData] = useState<QuestionTreeResponse | null>(null);
  const [isDisplayed, setIsDisplayed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // 503/502 del árbol: se pinta un aviso en la página (nunca navigate("/"), que
  // volvía a mandar aquí y repetía la petición en bucle).
  const [treeError, setTreeError] = useState<QuestionTreeErrorKindT | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const leadRepository = new HttpLeadRepository(leadId, token);

  const loadQuestionTree = async () => {
    if (!leadId || !token) return;

    setLoading(true);
    try {
      // TODO: Reemplazar con el endpoint real cuando esté disponible
      const response = await leadRepository.getQuestionPoduct();
      setTreeError(null);
      setTreeData(response);
    } catch (error) {
      trackSubmissionError(error);
      console.error("Error loading question tree:", error);
      const kind = questionTreeErrorKind(error);
      if (null !== kind) {
        setTreeError(kind);
      } else {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  // Reintento a mano (solo 503): vuelve a pedir el árbol; con el árbol ya
  // contestado el hook enseña el resumen y el cliente puede volver a confirmar.
  const retryQuestionTree = async () => {
    setIsRetrying(true);
    try {
      await loadQuestionTree();
    } finally {
      setIsRetrying(false);
    }
  };

  // Cargar el árbol de preguntas al montar
  useEffect(() => {
    loadQuestionTree();
  }, [leadId, token]);

  // Callback para guardar respuesta en el servidor
  const handleSaveAnswer = useCallback(
    async (questionId: number, questionName: string, value: string) => {
      try {
        await leadRepository.saveQuestion({
          answer: value,
          questionId,
        });

        // Contestada (también «ninguna»): nunca se manda la respuesta.
        trackQuestionAnswered(questionId, "product");

        // Tracking
        handleTrackerQuestion({
          type: "FS",
          event: "Question Answered",
          data: {
            questionId,
            stack: "ProductV2",
            leadId,
            page: window.location.href,
            platform: "App",
            answerId: value,
            answerString: value,
          },
        });

        handleTrackerQuestion({
          type: "GA",
          data: {
            event: "virtualPageview",
            questionId,
            stack: "ProductV2",
            leadId,
            answers: value,
          },
        });
      } catch (error) {
        trackSubmissionError(error);
        console.error("Error saving answer:", error);
        throw error;
      }
    },
    [leadId, leadRepository, handleTrackerQuestion]
  );

  // Callback para recargar datos del backend cuando se completan las preguntas
  const handleQuestionsCompleted = useCallback(async () => {
    try {
      const response = await leadRepository.getQuestionPoduct();
      setTreeData(response);
    } catch (error) {
      console.error("Error reloading question tree:", error);
      const kind = questionTreeErrorKind(error);
      if (null !== kind) {
        trackSubmissionError(error);
        setTreeError(kind);
      }
    }
  }, [leadRepository]);

  // Callback cuando el usuario confirma el resumen y quiere continuar
  const handleContinue = useCallback(async () => {
    setLoading(true);
    try {
      await leadRepository.confirmQuestionsProduct();
      navigate("/medicalQuestions");
    } catch (error) {
      trackSubmissionError(error);
      console.error("Error completing questions:", error);
      const kind = questionTreeErrorKind(error);
      if (null !== kind) {
        setTreeError(kind);
      }
    } finally {
      setLoading(false);
    }
  }, [getStatus, navigate, setLoading]);

  // Hook principal del árbol de preguntas
  const {
    currentQuestion,
    questions,
    isFirstQuestion,
    isCompleted,
    progress,
    setAnswer,
    goToNext,
    goToPrevious,
    goToQuestion,
    setIsCompleted,
    getCurrentAnswer,
  } = useQuestionTree({
    treeData,
    onSaveAnswer: handleSaveAnswer,
    onQuestionsCompleted: handleQuestionsCompleted,
  });

  // Actualizar progress bar global
  useEffect(() => {
    const progressValue = 30 + (progress * 0.4); // 30-70% del total
    setProgressBar(progressValue);
  }, [progress, setProgressBar]);

  // Emit question_viewed every time we show a new question
  useEffect(() => {
    if (!currentQuestion) return;
    trackQuestionViewed(currentQuestion.id, "product");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.id]);

  const handleGoBack = useCallback(() => {
    goToPrevious();
  }, [goToPrevious]);

  // Manejar cambio de respuesta
  const handleValues = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value.join(", ") : value;
    setAnswer(stringValue);
  };

  // Manejar siguiente pregunta con transición que cubre el tiempo de guardado
  const handleNextQuestion = async () => {
    setIsTransitioning(true);
    window.scrollTo(0, 0);

    // Ejecutar guardado y transición en paralelo
    // La transición visual cubre el tiempo de espera del backend
    await Promise.all([
      goToNext(),
      new Promise((resolve) => setTimeout(resolve, 200)), // Mínimo 200ms de transición
    ]);

    // Quitar transición para mostrar la nueva pregunta
    setIsTransitioning(false);
  };

  // Manejar edición de pregunta desde el resumen
  const handleEditQuestion = (questionId: number) => {
    setIsCompleted(false);
    goToQuestion(questionId);
  };

  if (null !== treeError) {
    return (
      <Container className="questions-outerContainer">
        <QuestionTreeErrorPanel
          kind={treeError}
          translate={translate}
          onRetry={retryQuestionTree}
          retrying={isRetrying}
        />
      </Container>
    );
  }

  // Si está completado, mostrar resumen
  if (isCompleted) {
    return (
      <Container className="questions-outerContainer">
        <QuestionsSummary
          questions={questions}
          answers={treeData?.answers || []}
          onContinue={handleContinue}
          onEditQuestion={handleEditQuestion}
          translate={translate}
          isLoading={globalLoading}
          locale={DOMAIN === "it" ? "it-IT" : DOMAIN === "pt" ? "pt-PT" : DOMAIN === "fr" ? "fr-FR" : DOMAIN === "en" ? "en-GB" : "es-ES"}
        />
      </Container>
    );
  }

  // Si no hay pregunta actual, mostrar loading
  if (!currentQuestion || globalLoading) {
    return null;
  }

  // Adaptar pregunta para FormBuilder
  const formBuilderQuestion = adaptQuestionForFormBuilder(currentQuestion);

  return (
    <Container className="questions-outerContainer">
      <div className="questions-outerContainer">
        {currentQuestion && (
          <FormBuilderProvider
            question={formBuilderQuestion}
            answer={getCurrentAnswer() || ""}
          >
            <div
              className={`questions-container ${
                isTransitioning ? "questions-transitioning" : "questions-visible"
              }`}
            >
              <div
                className={`questions-innerContainer ${
                  DOMAIN === "it" ? "questions-innerContainer-it" : ""
                }`}
              >
                <FormBuilder
                  key={currentQuestion.id}
                  answer={getCurrentAnswer() || ""}
                  capitalLimits={null}
                  chat={false}
                  countryList={null}
                  error={null}
                  handleSubmit={handleNextQuestion}
                  handleValues={handleValues}
                  onBlur={() => {}}
                  personalData={null}
                  question={formBuilderQuestion}
                  callback={(e: string) => {
                    handleValues(e);
                  }}
                  isOneChoinceQuestionRoute={false}
                  isDisplayed={isDisplayed}
                  setIsDisplayed={setIsDisplayed}
                  setValidateQuestionDateInput={() => {}}
                />
              </div>
              <FormButton
                showButton={true}
                isLoading={globalLoading}
                isOneChoinceQuestion={false}
                componentName={currentQuestion.component_name}
                translate={translate}
                handleSubmit={handleNextQuestion}
                question={formBuilderQuestion}
                answer={getCurrentAnswer() || ""}
                goBack={handleGoBack}
                customDisabled={false}
                questionStack={[]}
                isSticky={false}
                currentIndex={0}
              />
            </div>
          </FormBuilderProvider>
        )}
      </div>
    </Container>
  );
};

export default ProductQuestionsV2;
